import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Input from '../components/common/Input';
import { ResetPasswordApi } from '../services/accountService';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
 
  const hasRedirectedRef = useRef(false);
  const expiryTimerRef = useRef(null);
  const redirectTimerRef = useRef(null);
  const mountedTimeRef = useRef(null);

  
  const [resetData, setResetData] = useState({
    email: '',
    otp: '',
    isValid: false,
    timestamp: null
  });

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({
    password: '',
    confirmPassword: ''
  });

  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false
  });

  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasDigit: false,
    hasSpecialChar: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [apiSuccess, setApiSuccess] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(300); 
  const [showCountdown, setShowCountdown] = useState(false);

  // Format time remaining
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

 
  useEffect(() => {
  

    
    hasRedirectedRef.current = false;
    setIsRedirecting(false);
    setShowCountdown(false);
    
  
    if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);

    let finalEmail = '';
    let finalOtp = '';
    let finalTimestamp = null;
    let isValidData = false;

 
    const locationState = location.state;
    if (locationState) {
     
      finalEmail = locationState.email || '';
      finalOtp = locationState.otp || '';
      finalTimestamp = locationState.timestamp || new Date().getTime();
      isValidData = !!(finalEmail && finalOtp);
    }

   
    if (!isValidData) {
      try {
        const storedData = localStorage.getItem('resetPasswordData');
        if (storedData) {
          const parsedData = JSON.parse(storedData);
     
          
          finalEmail = parsedData.email || '';
          finalOtp = parsedData.otp || '';
          finalTimestamp = parsedData.timestamp || new Date().getTime();
          isValidData = !!(finalEmail && finalOtp);
        }
      } catch (error) {
        console.error('❌ Error reading localStorage:', error);
      }
    }

  
    if (isValidData && finalEmail && finalOtp && finalTimestamp) {
      // Calculate time elapsed
      const currentTime = new Date().getTime();
      const timeElapsed = Math.floor((currentTime - finalTimestamp) / 1000);
      const timeRemainingSeconds = Math.max(0, 300 - timeElapsed); 
      
   

      setResetData({
        email: finalEmail,
        otp: finalOtp,
        isValid: true,
        timestamp: finalTimestamp
      });
      
      setTimeRemaining(timeRemainingSeconds);
      
      if (timeRemainingSeconds > 0) {
       
        setShowCountdown(true);
        startExpiryTimer(finalTimestamp);
      } else {
      
      
        handleOtpExpiredRedirect();
      }
      
      setApiError('');
    } else {
    
     
      handleOtpExpiredRedirect();
    }

    // Cleanup function
    return () => {
      if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, [location]);

  // Start expiry timer
  const startExpiryTimer = (timestamp) => {
    if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
    
    expiryTimerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1;
        
        if (newTime <= 0) {
          // Time's up!
       
          clearInterval(expiryTimerRef.current);
          handleOtpExpiredRedirect();
          return 0;
        }
        
        // // Show warning in last 60 seconds
        // if (newTime <= 60) {
        //   setApiError(`OTP will expire in ${newTime} seconds. Please complete the process quickly.`);
        // }
        
        return newTime;
      });
    }, 1000);
  };

  // Function to handle OTP expired redirect
  const handleOtpExpiredRedirect = () => {
  
    
    if (!hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      setIsRedirecting(true);
      setShowCountdown(false);
      
      // Clear timers
      if (expiryTimerRef.current) {
        clearInterval(expiryTimerRef.current);
      }
      
  
      
      // Clear localStorage
      localStorage.removeItem('resetPasswordData');
      
      // Get current email before clearing
      const currentEmail = resetData.email;
      
      // Set redirecting message
      setApiError('OTP has expired. Redirecting to forgot password page...');
      
      // Navigate to forgot password page after 2 seconds
      redirectTimerRef.current = setTimeout(() => {
    
        navigate('/forgot-password', {
          state: { 
            email: currentEmail || '',
            fromResetPassword: true,
            message: 'Your OTP has expired. Please request a new one.'
          },
          replace: true
        });
      }, 2000);
    }
  };

  // Check password requirements
  const checkPasswordRequirements = (password) => {
    return {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasDigit: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      maxLength: password.length <= 20
    };
  };

  // Password validation function
  const validatePassword = (password) => {
    if (!password || password.trim() === '') {
      return 'Password is required';
    }

    if (password.length > 20) {
      return 'Password cannot exceed 20 characters';
    }

    const requirements = checkPasswordRequirements(password);
    const allValid = Object.values(requirements).every(req => req);

    if (!allValid) {
      return 'Password does not meet all requirements';
    }

    return '';
  };

  // Confirm password validation
  const validateConfirmPassword = (confirmPassword) => {
    if (!confirmPassword || confirmPassword.trim() === '') {
      return 'Please confirm your password';
    }

    if (confirmPassword !== formData.password) {
      return 'Passwords do not match';
    }

    return '';
  };

  // Real-time validation effect
  useEffect(() => {
    const validateField = (name, value) => {
      if (touched[name]) {
        if (name === 'password') {
          return validatePassword(value);
        } else if (name === 'confirmPassword') {
          return validateConfirmPassword(value);
        }
      }
      return '';
    };

    const newErrors = {
      password: validateField('password', formData.password),
      confirmPassword: validateField('confirmPassword', formData.confirmPassword)
    };

    setErrors(newErrors);
  }, [formData, touched]);

  // Update password requirements when password changes
  useEffect(() => {
    if (formData.password) {
      setPasswordRequirements(checkPasswordRequirements(formData.password));
      if (!showPasswordRequirements) {
        setShowPasswordRequirements(true);
      }
    } else {
      setPasswordRequirements({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasDigit: false,
        hasSpecialChar: false
      });
      setShowPasswordRequirements(false);
    }
  }, [formData.password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    if (apiError && !apiError.includes('OTP will expire')) {
      setApiError('');
    }

    if (!touched[name]) {
      setTouched(prevState => ({
        ...prevState,
        [name]: true
      }));
    }

    if (name === 'password' && value && !showPasswordRequirements) {
      setShowPasswordRequirements(true);
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    if (!touched[name]) {
      setTouched(prevState => ({
        ...prevState,
        [name]: true
      }));
    }

    if (name === 'password' && formData.password) {
      setShowPasswordRequirements(true);
    }
  };

  const handleFocus = (e) => {
    const { name } = e.target;
    if (name === 'password') {
      setShowPasswordRequirements(true);
    }
  };

  // Handle API errors with OTP expiry detection
  const handleApiError = (error) => {
    console.error('API Error:', error);

    if (error.response) {
      const errorData = error.response.data;
      let errorMessage = '';

      // Extract error message
      if (errorData.errors && errorData.errors.message) {
        if (Array.isArray(errorData.errors.message)) {
          errorMessage = errorData.errors.message[0];
        } else if (typeof errorData.errors.message === 'string') {
          errorMessage = errorData.errors.message;
        }
      } else if (errorData.message) {
        if (Array.isArray(errorData.message)) {
          errorMessage = errorData.message[0];
        } else if (typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
      }

      // Check if error indicates OTP expiry
      const otpExpiredKeywords = [
        'otp expired',
        'expired',
        'invalid otp',
        'otp is invalid',
        'timeout',
        'otp has expired',
        'session expired',
        'token expired'
      ];

      const isOtpExpired = otpExpiredKeywords.some(keyword => 
        errorMessage.toLowerCase().includes(keyword)
      );

      // Handle OTP expiry
      if (isOtpExpired || error.response.status === 401) {
    
        handleOtpExpiredRedirect();
        return;
      }

      // Handle other errors
      setApiError(errorMessage || 'Something went wrong. Please try again.');
    } else if (error.request) {
      setApiError('Network error. Please check your connection and try again.');
    } else {
      setApiError('An error occurred. Please try again.');
    }
  };

  // Reset password submit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    

    // Clear previous messages
    setApiError('');
    setApiSuccess('');
    setIsSubmitted(false);

    // Mark all fields as touched
    setTouched({
      password: true,
      confirmPassword: true
    });

    // Validate form
    const passwordError = validatePassword(formData.password);
    const confirmPasswordError = validateConfirmPassword(formData.confirmPassword);

    if (passwordError || confirmPasswordError) {
      setErrors({
        password: passwordError,
        confirmPassword: confirmPasswordError
      });
      return;
    }

    // Check if reset data is valid
    if (!resetData.email || !resetData.otp || !resetData.isValid) {
   
      setApiError('Reset session is invalid or expired. Please restart the password reset process.');
      handleOtpExpiredRedirect();
      return;
    }

    // Check if OTP is still valid based on time
    const currentTime = new Date().getTime();
    const timeElapsed = Math.floor((currentTime - resetData.timestamp) / 1000);
    
    if (timeElapsed > 300) { // 5 minutes
   
      handleOtpExpiredRedirect();
      return;
    }

    // Check password requirements
    const requirements = checkPasswordRequirements(formData.password);
    const allValid = Object.values(requirements).every(req => req);

    if (!allValid) {
      setApiError('Password does not meet all requirements.');
      return;
    }

    // Call API
    try {
      setIsLoading(true);

    

      const response = await ResetPasswordApi(
        resetData.email,
        resetData.otp,
        formData.password
      );

   

      // Clear localStorage and timers
      localStorage.removeItem('resetPasswordData');
      if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);

      // Show success
      setIsSubmitted(true);
      setApiSuccess('Password reset successful! Redirecting to login...');

      // Navigate to login
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 3000);

    } catch (error) {
      console.error('❌ Reset password error:', error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestartProcess = () => {
    // If already redirecting, don't do anything
    if (isRedirecting) return;
    
  
    localStorage.removeItem('resetPasswordData');
    if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
    
    navigate('/forgot-password', {
      state: { 
        email: resetData.email || '',
        message: 'Please request a new OTP to reset your password.'
      },
      replace: true
    });
  };

  const handleBackToLogin = () => {
    // If already redirecting, don't do anything
    if (isRedirecting) return;
    
    localStorage.removeItem('resetPasswordData');
    if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
    
    navigate('/', { replace: true });
  };

  // Render checkmark or X based on requirement status
  const RequirementItem = ({ met, text }) => (
    <li className={`requirement-item ${met ? 'requirement-met' : 'requirement-not-met'}`}>
      <span className="requirement-icon">
        {met ? '✓' : '✗'}
      </span>
      <span className="requirement-text">{text}</span>
    </li>
  );

  return (
    <div className="login-page">
      <div className="welcome-section">
        <div className="welcome-content">
          <h1 className="welcome-title">Welcome to OpenBook</h1>
        </div>
      </div>

      <div className="login-section">
        <div className="login-form-wrapper">
          <div className='rockwell_logo'>
            <img src='../../assets/Rockwell-Logo.jpg' alt='rockwell_logo' />
          </div>

          <div className="form-title-section">
            <h4 className="form-title">Reset Password</h4>
            <p className="form-subtitle">
              Create and confirm your new password.
            </p>
          </div>

          {/* OTP Timer Display */}
          {showCountdown && !isRedirecting && !isSubmitted && (
            <div className="otp-timer-container">
              <div className="otp-timer">
                <div className="timer-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={timeRemaining <= 60 ? "#f44336" : "#4CAF50"} strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <div className="timer-text">
                  OTP expires in: <span className={timeRemaining <= 60 ? 'timer-warning' : 'timer-normal'}>{formatTime(timeRemaining)}</span>
                </div>
              </div>
              {timeRemaining <= 60 && (
                <div className="timer-warning-message">
                  Hurry up! OTP will expire soon.
                </div>
              )}
            </div>
          )}

          {/* Display API success message */}
          {apiSuccess && (
            <div className="success-message">
              <div className="success-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <p className="success-text">
                {apiSuccess}
              </p>
            </div>
          )}

          {/* Display API error message */}
          {apiError && (
            <div className={`api-error-message ${isRedirecting ? 'redirecting' : ''}`}>
              {isRedirecting ? (
                <>
                  <div className="redirect-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF9800" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                  </div>
                  <span className="error-text">{apiError}</span>
                </>
              ) : (
                <span className="error-text">{apiError}</span>
              )}
            </div>
          )}

          {/* Only show form if we have valid reset data, not submitted, and not redirecting */}
          {resetData.isValid && !isSubmitted && !isRedirecting && (
            <form onSubmit={handleSubmit} className="login-form">
              <Input
                label={<span>New Password <span style={{ color: 'red' }}>*</span></span>}
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                maxLength={20}
                required
                placeholder="Enter new password"
                error={errors.password}
                disabled={isLoading}
                autoComplete="new-password"
              />

              {/* Password Requirements Checklist */}
              {showPasswordRequirements && (
                <div className="password-requirements-checklist">
                  <p className="requirements-title">Password must contain:</p>
                  <ul className="requirements-list">
                    <RequirementItem
                      met={passwordRequirements.minLength}
                      text="At least 8 characters long"
                    />
                    <RequirementItem
                      met={passwordRequirements.hasUpperCase}
                      text="One uppercase letter"
                    />
                    <RequirementItem
                      met={passwordRequirements.hasLowerCase}
                      text="One lowercase letter"
                    />
                    <RequirementItem
                      met={passwordRequirements.hasDigit}
                      text="One digit (0-9)"
                    />
                    <RequirementItem
                      met={passwordRequirements.hasSpecialChar}
                      text="One special character (!@#$%^&* etc.)"
                    />
                  </ul>
                </div>
              )}

              <Input
                label={<span>Confirm New Password <span style={{ color: 'red' }}>*</span></span>}
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                placeholder="Confirm new password"
                error={errors.confirmPassword}
                disabled={isLoading}
                autoComplete="new-password"
              />

              <div className="form-buttons">
                <button
                  type="submit"
                  className={`reset-btn ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading || isRedirecting}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span>
                      Processing...
                    </>
                  ) : 'Reset Password'}
                </button>
              </div>
            </form>
          )}

          {/* Show restart option when no valid reset data or OTP expired */}
          {(!resetData.isValid || isRedirecting) && !isSubmitted && (
            <div className="restart-process-section">
              <p className="restart-message">
                {isRedirecting 
                  ? 'OTP has expired. Redirecting to forgot password page...'
                  : 'Reset session has expired or is invalid. Please restart the password reset process.'
                }
              </p>
              <div className="form-buttons">
                <button
                  type="button"
                  className="reset-btn"
                  onClick={handleRestartProcess}
                  disabled={isRedirecting}
                >
                  {isRedirecting ? 'Redirecting...' : 'Restart Password Reset'}
                </button>

                <button
                  type="button"
                  className="back-btn-forgot"
                  onClick={handleBackToLogin}
                  disabled={isRedirecting}
                  style={{ 
                    cursor: isRedirecting ? 'not-allowed' : 'pointer', 
                    opacity: isRedirecting ? 0.5 : 1,
                    background: 'transparent',
                    border: 'none',
                    color: '#007bff',
                    textDecoration: 'underline',
                    padding: '10px',
                    fontSize: '14px'
                  }}
                >
                  Back to Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;